import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { hasAdminPermission } from '../../lib/adminPermissions';
import { useAuthStore } from '../../store/auth.store';
export const input =
  'h-9 rounded-md border border-gray-300 px-3 text-sm bg-white';
export const button =
  'inline-flex items-center justify-center rounded-md border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed';
export const primary = `${button} bg-primary text-white border-primary hover:bg-primary/90`;
export const labels: Record<string, string> = {
  draft: '草稿',
  pending_review: '待发布审核',
  approved: '已批准',
  rejected: '已驳回',
  paused: '暂停领取',
  offline: '已下架',
  active: '进行中',
  ended: '已结束',
  settled: '已结算（会员奖励）',
  archived: '已归档',
  admin: '总后台',
  agent: '商户',
  system: '系统',
};
export function useCan(p: string) {
  return hasAdminPermission(
    useAuthStore((s) => s.user),
    p,
  );
}
export function errorText(e: unknown) {
  const message =
    e && typeof e === 'object' && 'message' in e
      ? String(e.message)
      : '请求失败';
  return (
    (
      {
        agent_frozen: '商户业务已冻结',
        permission_denied: '当前账号无此操作权限',
        network_error: '网络连接失败，请重试',
        password_must_be_12_to_72_bytes: '密码需为 12–72 字节',
        invalid_request: '输入不合法，请检查填写内容',
        invalid_input: '输入不合法，请检查填写内容',
        invalid_advertising_input: '广告内容或筛选条件不合法，请检查素材、排期与日期范围',
        invalid_report_filter: '日期区间需在93天以内，结束时间应晚于开始时间',
        invalid_submission_date: '提交日期无效，请选择有效的日期',
        invalid_submission_date_range: '提交结束日期不能早于开始日期',
        evidence_unavailable: '证据尚未就绪或当前版本已撤销，请刷新',
        platform_unavailable: '发布服务暂不可用，请稍后重试',
        version_conflict: '数据已更新，请刷新后重新操作',
        forbidden: '当前状态或权限不允许此操作',
      } as Record<string, string>
    )[message] ?? `操作失败：${message}`
  );
}
export function useResource<T>(load: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setData(undefined);
    setError('');
    load()
      .then((v) => {
        if (active) setData(v);
      })
      .catch((e) => {
        if (active) setError(errorText(e));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [...deps, tick]);
  return { data, loading, error, reload: () => setTick((t) => t + 1) };
}
export function Panel({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5 space-y-4">
      {title && (
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      )}
      {children}
    </section>
  );
}
export function Notice({ error }: { error?: string }) {
  return error ? (
    <div role="alert" className="rounded-md bg-red-50 p-3 text-sm text-red-700">
      {error}
    </div>
  ) : null;
}
export function Badge({ value }: { value: string }) {
  return (
    <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700">
      {labels[value] ?? value}
    </span>
  );
}
export function Pager({
  total,
  page,
  change,
}: {
  total: number;
  page: number;
  change: (v: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / 20));
  return (
    <div className="flex justify-between items-center text-sm text-gray-500">
      <span>共 {total} 条</span>
      <div className="flex items-center gap-3">
        <button
          className={button}
          disabled={page <= 1}
          onClick={() => change(page - 1)}
        >
          上一页
        </button>
        {page} / {pages}
        <button
          className={button}
          disabled={page >= pages}
          onClick={() => change(page + 1)}
        >
          下一页
        </button>
      </div>
    </div>
  );
}
export function useFilters() {
  const [q, setQ] = useSearchParams();
  const set = (key: string, value: string) =>
    setQ(
      (p) => {
        const n = new URLSearchParams(p);
        value ? n.set(key, value) : n.delete(key);
        if (key !== 'page') n.delete('page');
        return n;
      },
      { replace: true },
    );
  return { q, set, page: Number(q.get('page')) || 1 };
}
export function SourceFilters() {
  const { q, set } = useFilters();
  return (
    <div className="flex flex-wrap gap-3">
      <select
        aria-label="任务来源"
        className={input}
        value={q.get('source') ?? ''}
        onChange={(e) => set('source', e.target.value)}
      >
        <option value="">全部来源</option>
        <option value="platform">平台任务</option>
        <option value="agent">商户任务</option>
      </select>
      <input
        aria-label="商户 ID"
        type="number"
        min="1"
        className={input}
        placeholder="商户 ID"
        value={q.get('agent_id') ?? ''}
        onChange={(e) => set('agent_id', e.target.value)}
      />
    </div>
  );
}
export function Provenance({
  item,
}: {
  item: {
    source?: string;
    agent_id?: number;
    agent_name?: string;
    agent_portal_revision_id?: number;
    agent_portal_task_id?: number;
    claim_revision_id?: number;
    reward_amount_minor?: number;
  };
}) {
  return (
    <span className="block text-xs font-normal text-gray-500 mt-1">
      {item.source === 'agent' || item.agent_id || item.agent_portal_task_id
        ? `商户 · ${item.agent_name || `#${item.agent_id ?? '-'}`} · 版本 #${item.claim_revision_id ?? item.agent_portal_revision_id ?? '-'}`
        : '平台任务'}
    </span>
  );
}
